import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { motion } from "motion/react";

import { hexToRgb } from "./Colors/colorUtils";

export default function FnrShineButton({
  onClick,
  children,
  borderRadius = 24,
  shineColor = "#FFFFFF",
  inset = 0,
  tapScale = 0.9,
  disabled,
  ...props
}) {
  const buttonRef = useRef();
  const measurements = useRef();

  useEffect(() => {
    measureButton();
  }, []);

  const [measured, setMeasured] = useState(false);
  const [tapping, setTapping] = useState(false);

  const hoverStart = (e) => {
    measureButton();
  };

  const measureButton = () => {
    measurements.current = buttonRef.current.getBoundingClientRect();
    setMeasured(true);
  };

  const updatePointer = ({ clientX, clientY, ...rest }) => {
    buttonRef.current.style.setProperty(
      "--fnrShineX",
      ((clientX - measurements.current.left) / measurements.current.width) *
        100 +
        "%"
    );
    buttonRef.current.style.setProperty(
      "--fnrShineY",
      ((clientY - measurements.current.top) / measurements.current.height) *
        100 +
        "%"
    );
  };

  const tapStart = () => setTapping(true);
  const tapEnd = () => setTapping(false);

  const shineRGB =
    typeof shineColor === "string" ? hexToRgb(shineColor) : shineColor;

  const styles = {
    "--fnrShineX": "50%",
    "--fnrShineY": "50%",
    "--fnrShineColorR": shineRGB.r,
    "--fnrShineColorG": shineRGB.g,
    "--fnrShineColorB": shineRGB.b,
    "--fnrShineBorderRadius": `${borderRadius}px`,
    "--fnrShineInset": inset,
  };

  if (measured) {
    styles["--fnrShineRadius"] =
      Math.max(80, measurements.current.width, measurements.current.height) *
        2 +
      "px";
  }

  const style = { ...styles };

  const handleClick = (e) => {
    if (!disabled && onClick) onClick();
  };

  return (
    <FnrShineButtonWrapper
      ref={buttonRef}
      onHoverStart={hoverStart}
      onTapStart={tapStart}
      onTapCancel={tapEnd}
      onTap={tapEnd}
      onClick={handleClick}
      onMouseMove={updatePointer}
      style={style}
      {...props}
    >
      <ShineWrapper>
        <Border />
        <Shine />
      </ShineWrapper>

      <ButtonContent
        animate={{
          scale: tapping ? tapScale : 1,
        }}
      >
        {children}
      </ButtonContent>
    </FnrShineButtonWrapper>
  );
}

export const FnrShineButtonWrapper = styled(motion.div)`
  cursor: pointer;
  position: relative;
  border-radius: var(--fnrShineBorderRadius);
`;
const ButtonContent = styled(motion.div)`
  z-index: 20;
  user-select: none;
  position: relative;
`;

const ShineWrapper = styled.div`
  position: absolute;
  inset: var(--fnrShineInset);
  border-radius: var(--fnrShineBorderRadius);
  z-index: 10;
  overflow: hidden;
`;

const Border = styled.div`
  border-radius: var(--fnrShineBorderRadius);
  position: absolute;
  inset: 0;
  z-index: 5;
  transition: box-shadow 1.2s;
  box-shadow: inset
    rgba(var(--fnrShineColorR), var(--fnrShineColorG), var(--fnrShineColorB), 0)
    0 0 1px;

  ${FnrShineButtonWrapper}:hover & {
    box-shadow: inset
      rgba(
        var(--fnrShineColorR),
        var(--fnrShineColorG),
        var(--fnrShineColorB),
        0.3
      )
      0 0 3px;
    transition: box-shadow 0.2s;
  }
`;

const Shine = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(
    var(--fnrShineRadius) circle at var(--fnrShineX) var(--fnrShineY),
    rgb(var(--fnrShineColorR), var(--fnrShineColorG), var(--fnrShineColorB)),
    transparent 70%
  );
  /* dumb old safari */
  @supports not (aspect-ratio: auto) {
    background: radial-gradient(
      calc(var(--fnrShineRadius) * 5) circle at var(--fnrShineX)
        var(--fnrShineY),
      rgb(var(--fnrShineColorR), var(--fnrShineColorG), var(--fnrShineColorB)),
      transparent 40%
    );
  }
  opacity: 0;
  transition:
    opacity 0.8s,
    background 0.2s;
  z-index: 3;

  ${FnrShineButtonWrapper}:hover & {
    opacity: 0.2;
    transition:
      opacity 0.4s,
      background 0.2s;
  }
`;
